import { useI18n } from "@core/i18n/i18n";
import {
	exerciseBodyPartsValues,
	exerciseCategories,
	exerciseDifficulties,
	exerciseStartingPositions,
} from "@domain/exercise/exercise";
import { getAllEquipmentNamesFromExercises } from "@domain/library/equipment";
import { ExerciseFilter } from "@domain/exercise/exercise-filter-manager";
import { FilterCategory } from "@domain/filters/filter";
import { useEffect, useMemo } from "react";
import { useObservable } from "micro-observables";
import {
	createBodyPartsFilter,
	createCategoriesFilter,
	createDifficultyFilter,
	createEquipmentFilter,
	createHelpNeededFilter,
	createStartingPositionFilter,
	createWithWheelchairFilter,
} from "@domain/exercise/exercise-filters";
import { ExerciseFetchState } from "@domain/exercise/exercise-manager";
import { exerciseManager } from "@core/service-container";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";

export const useExerciseFilters = (): FilterCategory<ExerciseFilter>[] => {
	const allExercises = useObservable(exerciseManager.allExercises);
	const exerciseFormatter = useExerciseFormatter();
	const { format } = useI18n();

	useEffect(() => {
		if (exerciseManager.exercisesStatus.get() === ExerciseFetchState.Undefined) {
			exerciseManager.fetchExercises();
		}
	}, []);

	const equipmentNames = useMemo(() => getAllEquipmentNamesFromExercises(allExercises), [allExercises]);

	return useMemo(
		() => [
			createCategoriesFilter(exerciseCategories, format("exercise.category.title"), exerciseFormatter.formatExerciseCategory),
			createBodyPartsFilter(exerciseBodyPartsValues, format("exercise.bodyPart.title"), exerciseFormatter.formatExerciseBodyPart),
			createStartingPositionFilter(
				exerciseStartingPositions,
				format("exercise.startingPosition.title"),
				exerciseFormatter.formatExerciseStartingPosition,
			),
			createWithWheelchairFilter(format("exercise.withWheelchair.title"), exerciseFormatter.formatExerciseWithWheelchair),
			createEquipmentFilter(equipmentNames, format),
			createHelpNeededFilter(format("exercise.helpNeeded.title"), exerciseFormatter.formatExerciseHelpNeeded),
			createDifficultyFilter(exerciseDifficulties, format("exercise.difficulty.title"), exerciseFormatter.formatExerciseDifficulty),
		],
		[
			equipmentNames,
			exerciseFormatter,
			format,
		],
	);
};
